/**
 * VALEN VARET · Progreso de la promocion por cantidad.
 *
 * Mantiene actualizados en vivo todos los widgets [data-vv-promo] de la pagina
 * (ficha de producto, carrito, cajon del carrito) cuando cambia el carrito:
 * al anadir, eliminar, subir o bajar la cantidad de un articulo.
 *
 * Los textos y los tramos NO se definen aqui: llegan desde Liquid en atributos
 * data-*, para que exista una unica fuente de verdad en snippets/vv-promo.liquid.
 */

/** Nombres de evento a los que reaccionamos (los del tema y los estandar de Shopify). */
const CART_EVENTS = new Set(['cart:update', 'cart:lines:update', 'cart:refresh']);

let subscribed = false;
/** @type {number|undefined} */
let refreshTimer;

function subscribe() {
  if (subscribed) return;
  subscribed = true;

  for (const name of CART_EVENTS) {
    document.addEventListener(name, scheduleRefresh);
  }

  // Al volver desde el historial el carrito puede haber cambiado.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleRefresh();
  });
}

function scheduleRefresh() {
  window.clearTimeout(refreshTimer);
  // Pequeno margen para que Shopify haya terminado de actualizar el carrito.
  refreshTimer = window.setTimeout(refresh, 120);
}

async function refresh() {
  const widgets = document.querySelectorAll('[data-vv-promo]');
  if (widgets.length === 0) return;

  try {
    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    const response = await fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;

    const cart = await response.json();
    const count = Number(cart?.item_count ?? 0);

    widgets.forEach((widget) => render(widget, count));
  } catch (error) {
    // Un fallo de red no debe romper la pagina: el servidor ya ha pintado un
    // estado valido y volvera a pintarlo en la siguiente recarga.
    console.warn('[vv-promo] No se pudo actualizar el progreso:', error);
  }
}

/**
 * Devuelve los tramos configurados desde Liquid.
 * @param {HTMLElement} widget
 * @returns {Array<Array<number>>} Pares [cantidad, porcentaje] ordenados.
 */
function getTiers(widget) {
  try {
    const tiers = JSON.parse(widget.dataset.vvTiers ?? '[]');
    return Array.isArray(tiers) ? tiers : [];
  } catch (_) {
    return [];
  }
}

/**
 * Repinta un widget con el numero de articulos indicado.
 * @param {HTMLElement} widget
 * @param {number} count
 */
function render(widget, count) {
  const tiers = getTiers(widget);
  if (tiers.length === 0) return;

  const maxQty = Number(tiers[tiers.length - 1][0]);
  const maxPct = Number(tiers[tiers.length - 1][1]);

  let currentPct = 0;
  let nextQty = 0;
  let nextPct = 0;

  for (const tier of tiers) {
    const qty = Number(tier[0]);
    const pct = Number(tier[1]);
    if (count >= qty) {
      currentPct = pct;
    } else if (nextQty === 0) {
      nextQty = qty;
      nextPct = pct;
    }
  }

  const missing = nextQty > 0 ? nextQty - count : 0;

  // Mensaje de progreso
  const message = widget.querySelector('[data-vv-message]');
  if (message) {
    let template = '';
    if (nextQty === 0) {
      template = widget.dataset.vvTplMax ?? '';
    } else if (currentPct > 0) {
      template = widget.dataset.vvTplBoth ?? '';
    } else if (count === 0) {
      template = widget.dataset.vvTplStart ?? '';
    } else {
      template = widget.dataset.vvTplNext ?? '';
    }

    message.innerHTML = template
      .replaceAll('{n}', String(missing))
      .replaceAll('{s}', missing === 1 ? '' : 's')
      .replaceAll('{p}', String(nextQty === 0 ? maxPct : nextPct))
      .replaceAll('{c}', String(currentPct));
  }

  // Barra de progreso hacia el descuento maximo
  const fill = widget.querySelector('[data-vv-fill]');
  if (fill instanceof HTMLElement) {
    const ratio = maxQty > 0 ? Math.min(count / maxQty, 1) : 0;
    fill.style.width = Math.round(ratio * 100) + '%';
  }

  // Estado de cada tramo de la tabla (si el widget la muestra)
  widget.querySelectorAll('[data-vv-tier-qty]').forEach((row) => {
    const qty = Number(row.dataset.vvTierQty);
    row.classList.toggle('is-reached', count >= qty);
    row.classList.toggle('is-next', qty === nextQty);
  });

  widget.dataset.vvCount = String(count);
}

// El evento estandar de Shopify se anade si el modulo esta disponible.
import('@shopify/events')
  .then((module) => {
    const name = module?.StandardEvents?.cartLinesUpdate;
    if (typeof name === 'string') CART_EVENTS.add(name);
    subscribe();
  })
  .catch(() => subscribe());